import { faPencilAlt, faPlus, faTrashAlt } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { EditSupplierModal, FindestRefTextBox, Supplier } from "Components/Shared";
import { LoggingConstants } from "Constants";
import { SupplierControllerSingleton } from "Controllers/SupplierController";
import { TechnologyActionTypeEnum, ToastTypeEnum } from "Enums";
import { PendoHelperSingleton, ToastHelperSingleton } from "Helpers";
import { useFetch } from "Hooks";
import { ISetTechnologyCountsAction } from "Interfaces";
import { FC, useContext, useEffect, useRef, useState } from "react";
import { CaseContext, TechnologyContext } from "Stores";
import { TOpenGraphMetadata, TSupplierDTO, TUseFetch } from "Types";

// styles
import styles from "./technologySuppliers.module.scss";

export const TechnologySuppliers: FC = () => {
    // Contexts
    const { caseState } = useContext(CaseContext);
    const { technologyState, technologyDispatch } = useContext(TechnologyContext);

    // Refs
    const newSupplierInputRef = useRef<HTMLInputElement>(null);

    // Let
    const { fetchedData }: TUseFetch<TSupplierDTO[]> = useFetch(`api/technologies/${caseState.selectedTechnologyId}/suppliers`);

    // State
    const [isEditSupplierModalOpen, setIsEditSupplierModalOpen] = useState(false);
    const [currentlyEditingSupplier, setCurrentlyEditingSupplier] = useState<TSupplierDTO | undefined>(undefined);
    const [currentSupplierUrl, setCurrentSupplierUrl] = useState("");
    const [currentOpenGraphMetadata, setCurrentOpenGraphMetadata] = useState<TOpenGraphMetadata | undefined>(undefined);
    const [suppliers, setSuppliers] = useState<TSupplierDTO[]>([]);

    // Logic
    useEffect(() => {
        // Update the list of supplier with the newly received data
        if (!fetchedData) return;
        setSuppliers(fetchedData);
    }, [fetchedData]);

    // Logging
    useEffect(() => {
        // Log getting the technology suppliers
        PendoHelperSingleton.trackEventWithProperties(`${LoggingConstants.DYNAMIC_DELIVERY}TechnologyGetSuppliers`, {
            technologyId: caseState.selectedTechnologyId
        });
    }, [caseState.selectedTechnologyId]);

    const onCreateSupplierClick = async () => {
        // Check if the input reference is not null
        if (!newSupplierInputRef.current) return;
        // Reset the current state
        setCurrentlyEditingSupplier(undefined);
        setCurrentOpenGraphMetadata(undefined);
        // Retrieve the url from the input and trim the whitespaces
        const currentInputUrl = newSupplierInputRef.current.value.trim();
        // Check if the user entered something as url otherwise show an error
        if (currentInputUrl.length === 0) {
            ToastHelperSingleton.showToast(ToastTypeEnum.Error, "Please enter a url of a supplier.");
            return;
        }
        // Set the url of the supplier to the url of the input
        setCurrentSupplierUrl(currentInputUrl);
        // Retrieve the open graph metadata of the url
        const openGraphMetadata = await SupplierControllerSingleton.getOpenGraphAsync(currentInputUrl);
        // Set the retrieved open graph data to the new supplier
        const newSupplier: TSupplierDTO = {
            id: -1,
            url: currentInputUrl,
            companyName: openGraphMetadata?.siteName,
            pageTitle: openGraphMetadata?.title,
            faviconUrl: openGraphMetadata?.favicon,
            pageDescription: openGraphMetadata?.description,
            pageImageUrl: openGraphMetadata?.image
        };
        // Set the retrieved open graph data to the current open graph metadata
        setCurrentOpenGraphMetadata(openGraphMetadata);
        onCreateSupplierHandler(newSupplier);
    };

    const onStartEditSupplier = async (supplier: TSupplierDTO) => {
        // Set the supplier that is currently being edited
        setCurrentlyEditingSupplier(supplier);
        // Open the modal to edit the supplier
        setIsEditSupplierModalOpen(true);
    };

    const onDeleteSupplier = async (supplierId: number) => {
        // Create the technology on the server
        const isSuccess = await SupplierControllerSingleton.deleteAsync(supplierId);
        if (!isSuccess) {
            // If the update was not created successfully, show an error and do not update the interface
            ToastHelperSingleton.showToast(ToastTypeEnum.Error, "Failed to delete supplier.");
            return;
        }
        // Remove the supplier using a filter to create a new object so the state will change for react
        const newSuppliers = suppliers.filter((supplier) => supplier.id !== supplierId);
        setSuppliers(newSuppliers);
        // Update the supplier count of the current technology to reflect the removal
        technologyState.counts.supplierCount -= 1;
        technologyDispatch({ type: TechnologyActionTypeEnum.SetTechnologyCounts, counts: technologyState.counts } as ISetTechnologyCountsAction);
    };

    // Save functions:
    const onCreateSupplierHandler = async (supplier: TSupplierDTO) => {
        // Get the technology id of the currently selected technology
        const technologyId = caseState.selectedOriginalTechnologyId ? caseState.selectedOriginalTechnologyId : caseState.selectedTechnologyId;
        if (!technologyId) return;

        // Create the new supplier on the server and in the database
        const newSupplier = await SupplierControllerSingleton.createAsync(technologyId, supplier);
        // Add the newly created supplier to the list of suppliers
        setSuppliers([...suppliers, newSupplier]);

        // Update the supplier count of the current technology to reflect the addition
        technologyState.counts.supplierCount += 1;
        technologyDispatch({ type: TechnologyActionTypeEnum.SetTechnologyCounts, counts: technologyState.counts } as ISetTechnologyCountsAction);

        // Reset the newSupplier
        if(newSupplierInputRef.current) newSupplierInputRef.current.value = "";
    };

    const onUpdateSupplierHandler = async (supplier: TSupplierDTO) => {
        // Update the supplier on the server
        const isSuccess = await SupplierControllerSingleton.updateAsync(supplier);
        if (!isSuccess) {
            // If the update was not successful, show an error and do not update the interface
            ToastHelperSingleton.showToast(ToastTypeEnum.Error, "Failed to update supplier.");
            return;
        }

        // Update the supplier list using a map to make sure a new object is created
        const newSuppliers = suppliers.map((currentSupplier: TSupplierDTO) => {
            if(currentSupplier.id === supplier.id) {
                currentSupplier = supplier;
            }
            return currentSupplier;
        });

        // Update the suppliers list state
        setSuppliers([...newSuppliers]);
    };

    return (
        <div className={styles.supplierTabContentContainer}>
            <EditSupplierModal isOpen={isEditSupplierModalOpen} setIsOpen={setIsEditSupplierModalOpen} metadata={currentOpenGraphMetadata}
                url={currentSupplierUrl} onUpdate={onUpdateSupplierHandler} editingSupplier={currentlyEditingSupplier} />
            {suppliers.map((supplier: TSupplierDTO) => {
                return (
                    <div className={styles.supplierMainContainer} key={`supplier${supplier.id}`}>
                        <Supplier isEditMode={false} supplier={supplier} />
                        <div className={[styles.supplierEditButton, styles.editSupplier].join(" ")} title="Edit supplier" onClick={() => { onStartEditSupplier(supplier); }}>
                            <FontAwesomeIcon icon={faPencilAlt} />
                        </div>
                        <div className={[styles.supplierEditButton, styles.deleteSupplier].join(" ")} title="Delete supplier" onClick={() => { onDeleteSupplier(supplier.id); }}>
                            <FontAwesomeIcon icon={faTrashAlt} />
                        </div>
                    </div>
                );
            })}
            <div className={styles.addNewSupplier} title="Add Supplier URL">
                <div className={styles.addNewSupplierButton} onClick={onCreateSupplierClick}>
                    <FontAwesomeIcon icon={faPlus} />
                </div>
                <FindestRefTextBox ref={newSupplierInputRef} placeholder="Paste URL" onEnter={onCreateSupplierClick} extraClassNameInputElement={styles.addNewSupplierInput} />
            </div>
        </div>
    );
};